import { withDependencyWrites } from './write-intents';
import type { PortEnv, PortWithWrites } from '../../port-contract';
import { calculateVitalityStatus } from '../../compile/vitality-reader';
import { intval, json_encode, strval, strcasecmp, empty, max } from '../../php-compat';
import { array, catalogId, get, values, isArray, type Storage, type ReadEnv } from './shared';

// Immutable catalog entries from additions.php VitalityStatus::LIST, in PHP order.
const tiers = [
    ['invigorated', '82788F70-EBEF-44C4-80E7-6622AD20DD60'], ['fresh', 'FF30B76E-785D-2CAF-FC66-C131D0548808'],
    ['barely_wounded', '658483AA-34DE-74E5-C974-E3E202C31CC7'], ['wounded', 'C97076EF-FED9-19B4-D109-C22F9BC740E8'],
    ['bloodied', '44B8792E-632E-2651-C666-67DFD181E92A'], ['well_bloodied', '298BE1DD-2917-476E-D79C-92CF8C598926'],
    ['on_deaths_door', 'D5DE14C4-3194-87BC-D767-6D45BDD90552'], ['dying', '8AE129AF-52D8-CCE8-706A-85767DB52740'],
    ['dead', '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA'],
] as const;
/** PHP: ft_contingency_pack_condition (includes/functions/janus/core/sanguine_contingency.php:48) */
export function ftContingencyPackCondition(cond: string, reserved: number | bigint, level: number | bigint = 0, self = ''): string {
    return strval(json_encode(array(['t', self], ['c', cond], ['m', max(0, reserved)], ['l', max(0, level)])));
}
/** PHP: ft_contingency_vstatus_index (includes/functions/janus/core/sanguine_contingency.php:109) */
export function ftContingencyVstatusIndex(variable: string, env: Pick<PortEnv<object>, 'functionStatics'>): number | null {
    const key = 'ft_contingency_vstatus_index::$index';
    if (env.functionStatics[key] == null) env.functionStatics[key] = array(...tiers.map(([name], index) => [name, index] as [string, number]));
    return (get(env.functionStatics[key], variable) ?? null) as number | null;
}
/** PHP: ft_contingency_condition_is_valid (includes/functions/janus/core/sanguine_contingency.php:122) */
export function ftContingencyConditionIsValid(cond: string, env: Pick<PortEnv<object>, 'functionStatics'>): boolean {
    return cond === 'combat_begins' || cond === 'combat_ends'
        || cond.startsWith('vstatus:') && ftContingencyVstatusIndex(cond.slice(8), env) !== null;
}
/** PHP: ft_contingency_current_vstatus_severity (includes/functions/janus/core/sanguine_contingency.php:138) */
export function ftContingencyCurrentVstatusSeverity(caster: Storage, env: ReadEnv): number | null {
    const cur = env.deps.computed_value(caster, 'vitality', 'v_current');
    const maximum = env.deps.computed_value(caster, 'vitality', 'effective_v_max') ?? env.deps.computed_value(caster, 'vitality', 'v_max');
    const temp = env.deps.computed_value(caster, 'vitality', 'v_temp') ?? 0;
    if (cur == null || maximum == null) return null;
    const calculated = calculateVitalityStatus(intval(cur), intval(maximum), intval(temp));
    const tier = tiers.find(([, id]) => id === calculated)!;
    return ftContingencyVstatusIndexForStatus(catalogId(env, 'VitalityStatus', tier[0], calculated)!);
}
/** PHP: ft_contingency_vstatus_index_for_status (includes/functions/janus/core/sanguine_contingency.php:156) */
export function ftContingencyVstatusIndexForStatus(id: string): number | null {
    const at = tiers.findIndex(([, status]) => status === id);
    return at < 0 ? null : at;
}
/** PHP: ft_contingency_arming_item_ids (includes/functions/janus/core/sanguine_contingency.php:171) */
export function ftContingencyArmingItemIds(): Storage {
    return array([0, 'DC9778C6-5B9B-1501-B86A-5EEF4671F66A'], [1, '3BDE1AA4-23D6-C4E8-CADD-ED15B4403EA6']);
}
/** PHP: ft_contingency_row_from_status (includes/functions/janus/core/sanguine_contingency.php:183) */
export function ftContingencyRowFromStatus(row: Storage): Storage {
    return array(['trigger_id', strval(get(row, 'status_id') ?? '')], ['item_id', strval(get(row, 'status_origin_id') ?? '')],
        ['use_ability_variable_1', strval(get(row, 'status_casting_targets_arr') ?? '')],
        ['caster_encounter_creature_id', strval(get(row, 'status_target_id') ?? '')], ['arming_item_id', strval(get(row, 'status_item_id') ?? '')]);
}
/** PHP: ft_contingency_spell_is_self_castable (includes/functions/janus/core/sanguine_contingency.php:442) */
export function ftContingencySpellIsSelfCastable(spell: Storage, env: { globals: Record<string, unknown>; deps: Pick<ReadEnv['deps'], 'layer_value'> }): boolean {
    const range = env.deps.layer_value(spell, 'range');
    if (range === null || range === '') return true;
    const self = catalogId(env, 'RangeType', 'self', 'D82855E1-4BB9-93EA-6374-B9904318BC1D');
    return typeof self === 'string' && strcasecmp(strval(range), self) === 0;
}
/** PHP: ft_contingency_spell_has_x_placeholder (includes/functions/janus/core/sanguine_contingency.php:463) */
export function ftContingencySpellHasXPlaceholder(spell: Storage): boolean {
    const walk = (v: unknown): boolean => typeof v === 'string' ? /\[x\]/i.test(v) : values(v).some(walk);
    return walk(get(spell, 'item_layer_types') ?? []);
}
/** PHP: ft_contingency_spell_max_cast_level (includes/functions/janus/core/sanguine_contingency.php:491) */
export function ftContingencySpellMaxCastLevel(spell: Storage, env: { deps: Pick<ReadEnv['deps'], 'layer_value'> & { ft_spell_is_level_upcastable(spell: Storage): boolean } }): number | bigint {
    const level = intval(env.deps.layer_value(spell, 'spell_level') ?? 0);
    if (level < 1 || !env.deps.ft_spell_is_level_upcastable(spell)) return level < 0 ? 0 : level;
    return ftContingencySpellHasXPlaceholder(spell) ? level : level < 5 ? 5 : level;
}

import * as php from '../../php-compat';
import { call, r, put, append, type Ref, type DispatchEnv } from './dispatch';

/** PHP: ft_contingency_refund_mana (includes/functions/janus/core/sanguine_contingency.php:379) */
export function ftContingencyRefundMana(caster: unknown,amount: number,affected: Ref,env: DispatchEnv): PortWithWrites<number> {
 return withDependencyWrites(env, env => {
 if(amount<=0||!php.truthy(env.deps.check_uuid(caster)))return 0;
 const creature={value:call(env,'query_d_item_arr',{item_id:caster,source_error:'janus:contingency_refund'})};
 if(php.empty(creature.value)||php.empty(get(creature.value,'encounter_creatures')))return 0;
 const current=Number(php.intval(get(creature.value,'encounter_creatures','mana')??0)),max=get(creature.value,'encounter_creatures','mana_total')??null;
 let next=current+amount;if(php.is_numeric(max))next=Math.min(next,Number(php.intval(max)));if(next<=current)return 0;
 put(creature,['encounter_creatures','mana'],next);
 call(env,'update_encounter_creature_stats',{encounter_creature_arr:r(creature),updates:array(['mana',get(creature.value,'encounter_creatures','mana')]),affected_ids:r(affected)});return next-current;

 });
}
/** PHP: ft_expire_contingency_triggers_for_encounter (includes/functions/janus/core/sanguine_contingency.php:415) */
export function ftExpireContingencyTriggersForEncounter(encounter: unknown,refund: unknown,affected: Ref,env: DispatchEnv): PortWithWrites<null> {
 return withDependencyWrites(env, env => {
 if(!php.truthy(env.deps.check_uuid(encounter)))return null;
 for(const row of values(env.deps.ft_fetch_all_active_contingency_triggers_for_encounter(encounter))){const id=php.strval(get(row,'trigger_id')??'');
 if(!php.truthy(env.deps.check_uuid(id))||!php.truthy(env.deps.ft_contingency_claim_status(id)))continue;
 if(php.truthy(refund)){const parsed=env.deps.ft_contingency_parse_condition(get(row,'use_ability_variable_1')??null);env.deps.ft_contingency_refund_mana(php.strval(get(row,'caster_encounter_creature_id')??''),php.intval(get(parsed,'mana')),affected);}}
return null;

 });
}
/** PHP: ft_contingency_eligible_spells (includes/functions/janus/core/sanguine_contingency.php:507) */
export function ftContingencyEligibleSpells(caster: unknown,arming: unknown='DC9778C6-5B9B-1501-B86A-5EEF4671F66A',env: { deps: {
 layer_entries(caster: unknown, bucket: string): unknown;
 item_field(entry: unknown, key: string): unknown;
 ability_entry_id(entry: unknown): unknown;
 check_uuid(id: unknown): unknown;
 ft_contingency_arm_authorize(caster: unknown, id: string, condition: string, level: null, arming: unknown): unknown;
} }): Storage {
 const out=array(),seen=array();
 for(const bucket of ['spells_known','at_will_spell','innate_spell_per_long_rest','spell_prepared_1'])for(const entry of values(env.deps.layer_entries(caster,bucket))){
 let id='';if(typeof entry==='string')id=entry;else if(isArray(entry))id=php.strval(env.deps.item_field(entry,'item_id')??get(entry,'item_id')??env.deps.ability_entry_id(entry)??get(entry,0)??'');
 if(id===''||!php.truthy(env.deps.check_uuid(id))||php.isset(seen,id))continue;seen.set(id,true);
 const auth=env.deps.ft_contingency_arm_authorize(caster,id,'combat_begins',null,arming);if(get(auth,'ok')===true)out.append(get(auth,'spell_arr'));}return out;
}

/** PHP: {closure@465:1600} (includes/functions/janus/core/sanguine_contingency.php:465) */
export function closure465_1600(value:unknown):boolean {return typeof value==='string'?php.strtolower(value).includes('[x]'):isArray(value)&&values(value).some(closure465_1600);}
